/**
 * usePoolDispatch · the live Lead Pool endpoints (sales-lead-pool-dispatch §3).
 *
 *   usePoolPulse()            GET  /api/v1/leads/pool/pulse          — 30 s poll
 *   useLiveLeads(temp, queue) GET  /api/v1/leads/pool?temperature=…  — 30 s poll
 *   usePoolLeaderboard()      GET  /api/v1/leads/pool/leaderboard    — 30 s poll
 *   usePoolDispatchBoard()    GET  /api/v1/leads/pool/dispatch       — 30 s poll (managers)
 *   useClaimNext()            POST /api/v1/leads/pool/claim-next
 *   useAutoDistribute()       POST /api/v1/leads/pool/auto-distribute
 *   useDispatchPolicy()       GET/PUT /api/v1/sales-settings/dispatch-policy
 *
 * Polls pause while the tab is hidden and catch up the moment it is shown again (L6). A failed
 * poll keeps the last good data on screen and reports the error beside it — a live board that
 * blanks itself on one dropped request is worse than one that is a few seconds stale.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { authFetch } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { emitLeadChange, onLeadChange } from '../leadBus'
import {
  claimNextBody,
  liveListParams,
  normalizeAutoDistribute,
  normalizeClaimNext,
  normalizeLeaderboard,
  repName,
  shouldPoll,
} from '../poolDispatch'
import {
  DEFAULT_DISPATCH_POLICY,
  type ApiResponse,
  type AutoDistributeResult,
  type ClaimNextResult,
  type DispatchPolicyApi,
  type LeaderboardEntry,
  type PaginatedResponse,
  type PoolDispatch,
  type PoolLeadItem,
  type PoolPulse,
  type PoolQueueKey,
  type PoolTemperature,
  type SettableCustomerType,
} from '../types'
import { toPoolRefusal, type PoolRefusal } from './useLeadPool'
import { publishPulse } from './useHotLeadBadge'
import { publishDispatchPolicy } from './useAtHoldingCap'

/** L6: the board refreshes every 30 seconds while it is visible. */
export const LIVE_POLL_MS = 30_000

/**
 * Run `callback` now (even in a hidden tab), then every `intervalMs` while the document is visible; when it becomes
 * visible again after being hidden longer than that, run it straight away. A change of
 * `resetKey` (e.g. the request path) starts over with an immediate run.
 */
export function useVisibleInterval(
  callback: () => void,
  intervalMs: number,
  enabled: boolean,
  resetKey?: string | null,
): void {
  const cbRef = useRef(callback)
  useEffect(() => {
    cbRef.current = callback
  })
  useEffect(() => {
    if (!enabled) return
    let last: number | null = null
    const tick = () => {
      const now = Date.now()
      if (!shouldPoll(last, now, intervalMs, document.hidden)) return
      last = now
      cbRef.current()
    }
    // The FIRST read always happens, visible or not — a board opened in a background tab must
    // still load. Only the repeating polls wait for the tab to be visible.
    last = Date.now()
    cbRef.current()
    const id = window.setInterval(tick, intervalMs)
    const onVisibility = () => {
      if (!document.hidden) tick()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [intervalMs, enabled, resetKey])
}

/** A ticking clock for relative times ("4 min", "updated 12 s ago"). */
export function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}

export interface PolledState<T> {
  data: T | null
  /** False until the first response for the CURRENT path (data or error) has arrived. */
  hasLoaded: boolean
  /** The last refresh failed — `data` is the previous good response, if any. */
  error: string | null
  /** When `data` last arrived (ms epoch). */
  updatedAt: number | null
  refetch: () => Promise<void>
}

/**
 * GET `path` now, on every lead write (leadBus), and every `intervalMs` while visible. `path`
 * null = disabled. Data is tagged with the path it came from, so a queue switch never shows the
 * previous queue's rows as if they were current.
 */
export interface PolledGetOptions<T> {
  /** Default LIVE_POLL_MS (30 s). */
  intervalMs?: number
  /** Called with every good response. */
  onData?: (data: T) => void
  /** Re-read on any lead write (leadBus). Default true. */
  followLeadChanges?: boolean
}

export function usePolledGet<T>(path: string | null, options: PolledGetOptions<T> = {}): PolledState<T> {
  const { intervalMs = LIVE_POLL_MS, onData, followLeadChanges = true } = options
  const token = useAuthStore((s) => s.tokens?.accessToken)
  const [state, setState] = useState<{
    path: string | null
    data: T | null
    error: string | null
    updatedAt: number | null
  }>({ path: null, data: null, error: null, updatedAt: null })
  const seq = useRef(0)
  const onDataRef = useRef(onData)
  useEffect(() => {
    onDataRef.current = onData
  })

  const fetchNow = useCallback(async () => {
    if (!path || !token) return
    const mine = ++seq.current
    try {
      const resp = await authFetch<ApiResponse<T>>(path, token, { suppressPermissionToast: true })
      if (mine !== seq.current) return
      if (resp.success && resp.data !== undefined && resp.data !== null) {
        setState({ path, data: resp.data, error: null, updatedAt: Date.now() })
        onDataRef.current?.(resp.data)
      } else {
        setState((prev) => ({ ...prev, path, error: resp.message ?? 'Failed to load' }))
      }
    } catch (e) {
      if (mine !== seq.current) return
      const message = e instanceof Error ? e.message : 'Failed to load'
      // Keep the previous data only when it belongs to this same path.
      setState((prev) => (prev.path === path ? { ...prev, error: message } : { path, data: null, error: message, updatedAt: null }))
    }
  }, [path, token])

  // The first read happens here too (immediately, and again whenever the path changes).
  useVisibleInterval(() => void fetchNow(), intervalMs, !!path && !!token, path)
  useEffect(() => (followLeadChanges ? onLeadChange(() => void fetchNow()) : undefined), [fetchNow, followLeadChanges])

  const current = state.path === path
  return {
    data: current ? state.data : null,
    hasLoaded: current && (state.data !== null || state.error !== null),
    error: current ? state.error : null,
    updatedAt: current ? state.updatedAt : null,
    refetch: fetchNow,
  }
}

// ─── Readers ───────────────────────────────────────────────────────────────────────

export function usePoolPulse(enabled = true): PolledState<PoolPulse> {
  // Every pulse read also feeds the sidebar badge, so the two never disagree.
  return usePolledGet<PoolPulse>(enabled ? '/api/v1/leads/pool/pulse' : null, { onData: publishPulse })
}

export interface LiveLeadsState {
  leads: PoolLeadItem[]
  totalElements: number
  hasLoaded: boolean
  error: string | null
  updatedAt: number | null
  refetch: () => Promise<void>
}

/** One temperature of the live board, unowned only, newest-waiting first (server's default sort). */
export function useLiveLeads(temperature: PoolTemperature, queue: PoolQueueKey, size: number): LiveLeadsState {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(liveListParams(temperature, queue, size))) q.set(k, String(v))
  const res = usePolledGet<PaginatedResponse<PoolLeadItem>>(`/api/v1/leads/pool?${q.toString()}`)
  return {
    leads: res.data?.content ?? [],
    totalElements: res.data?.totalElements ?? 0,
    hasLoaded: res.hasLoaded,
    error: res.error,
    updatedAt: res.updatedAt,
    refetch: res.refetch,
  }
}

export function usePoolLeaderboard(enabled = true): {
  entries: LeaderboardEntry[]
  hasLoaded: boolean
  error: string | null
} {
  const res = usePolledGet<unknown>(enabled ? '/api/v1/leads/pool/leaderboard' : null)
  return { entries: normalizeLeaderboard(res.data), hasLoaded: res.hasLoaded, error: res.error }
}

export function usePoolDispatchBoard(enabled: boolean): PolledState<PoolDispatch> {
  return usePolledGet<PoolDispatch>(enabled ? '/api/v1/leads/pool/dispatch' : null)
}

/**
 * Who a leader may hand pool leads to — the dispatch board's reps, which the server builds from
 * the same rule its assign endpoint enforces (sales users; team-only for a TEAM-view manager), so
 * the picker never offers someone the write answers 403 OUT_OF_TEAM for. Read while `enabled`
 * (an open "Assign to…" popover) only.
 */
export function useDispatchAssignees(enabled: boolean): {
  users: { userId: string; name: string }[]
  isLoading: boolean
  error: string | null
} {
  const board = usePoolDispatchBoard(enabled)
  const users = useMemo(
    () =>
      (board.data?.reps ?? [])
        .map((r) => ({ userId: r.userId, name: repName(r) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [board.data],
  )
  return { users, isLoading: enabled && !board.hasLoaded, error: board.error }
}

// ─── Writers ───────────────────────────────────────────────────────────────────────

export function useClaimNext() {
  const token = useAuthStore((s) => s.tokens?.accessToken)
  const [claiming, setClaiming] = useState(false)
  const inFlight = useRef(false)

  const claimNext = useCallback(
    async (queue: PoolQueueKey): Promise<PoolRefusal<ClaimNextResult>> => {
      if (!token) return { success: false, message: 'Not authenticated' }
      if (inFlight.current) return { success: false, message: 'Already claiming' }
      inFlight.current = true
      setClaiming(true)
      try {
        const resp = await authFetch<ApiResponse<unknown>>('/api/v1/leads/pool/claim-next', token, {
          method: 'POST',
          body: JSON.stringify(claimNextBody(queue)),
          suppressPermissionToast: true,
        })
        const result = resp.success ? normalizeClaimNext(resp.data) : null
        if (result) {
          emitLeadChange()
          return { success: true, data: result, message: resp.message }
        }
        return { success: false, message: resp.message, errorCode: resp.errorCode }
      } catch (e) {
        return toPoolRefusal(e, 'Could not claim the next lead')
      } finally {
        inFlight.current = false
        setClaiming(false)
      }
    },
    [token],
  )

  return { claimNext, claiming }
}

/**
 * A leader's Assign — the dispatch board, and "Assign to…" on the backlog, the live board and the
 * drawer: POST /api/v1/leads/pool/{leadId}/assign {userId, customerType?} (managers).
 * `customerType` is only used for an untyped company: the server sets it, then assigns.
 * Refusals: 409 ALREADY_CLAIMED / ACCOUNT_COMPANY, 422 CUSTOMER_TYPE_REQUIRED / ASSIGNEE_NOT_SALES /
 * INVALID_CUSTOMER_TYPE, 403 OUT_OF_TEAM.
 */
export function usePoolAssign() {
  const token = useAuthStore((s) => s.tokens?.accessToken)

  const assignFromPool = useCallback(
    async (
      leadId: string,
      userId: string,
      opts: {
        customerType?: SettableCustomerType
        /** Bulk runs announce once at the end instead of once per row. Default true. */
        announce?: boolean
      } = {},
    ): Promise<PoolRefusal<PoolLeadItem>> => {
      if (!token) return { success: false, message: 'Not authenticated' }
      const announce = opts.announce ?? true
      try {
        const resp = await authFetch<ApiResponse<PoolLeadItem>>(
          `/api/v1/leads/pool/${encodeURIComponent(leadId)}/assign`,
          token,
          {
            method: 'POST',
            body: JSON.stringify(opts.customerType ? { userId, customerType: opts.customerType } : { userId }),
            suppressPermissionToast: true,
          },
        )
        if (resp.success) {
          if (announce) emitLeadChange()
          return { success: true, data: resp.data, message: resp.message }
        }
        return { success: false, message: resp.message, errorCode: resp.errorCode }
      } catch (e) {
        return toPoolRefusal(e, 'Could not assign')
      }
    },
    [token],
  )

  return { assignFromPool }
}

export function useAutoDistribute() {
  const token = useAuthStore((s) => s.tokens?.accessToken)
  const [running, setRunning] = useState(false)

  const autoDistribute = useCallback(
    async (body: { leadIds: string[] } | { count: number }): Promise<PoolRefusal<AutoDistributeResult>> => {
      if (!token) return { success: false, message: 'Not authenticated' }
      setRunning(true)
      try {
        const resp = await authFetch<ApiResponse<unknown>>('/api/v1/leads/pool/auto-distribute', token, {
          method: 'POST',
          body: JSON.stringify(body),
          suppressPermissionToast: true,
        })
        if (resp.success) {
          emitLeadChange()
          return { success: true, data: normalizeAutoDistribute(resp.data), message: resp.message }
        }
        return { success: false, message: resp.message, errorCode: resp.errorCode }
      } catch (e) {
        return toPoolRefusal(e, 'Could not auto-distribute')
      } finally {
        setRunning(false)
      }
    },
    [token],
  )

  return { autoDistribute, running }
}

/** Fill any field the server left out with the plan's default, so the UI never reads undefined. */
function withDefaults(p: Partial<DispatchPolicyApi> | null | undefined): DispatchPolicyApi {
  return { ...DEFAULT_DISPATCH_POLICY, ...(p ?? {}) }
}

/** The policy changes rarely; re-read every 5 minutes so an open board picks up a new SLA. */
const POLICY_POLL_MS = 5 * 60_000

export function useDispatchPolicy() {
  const token = useAuthStore((s) => s.tokens?.accessToken)
  const res = usePolledGet<Partial<DispatchPolicyApi>>('/api/v1/sales-settings/dispatch-policy', {
    intervalMs: POLICY_POLL_MS,
    followLeadChanges: false,
  })
  const { refetch } = res

  const savePolicy = useCallback(
    // The server merges a partial body against the saved policy (omitted = unchanged), so a
    // single switch can be saved on its own.
    async (next: Partial<DispatchPolicyApi>): Promise<PoolRefusal<Partial<DispatchPolicyApi>>> => {
      if (!token) return { success: false, message: 'Not authenticated' }
      try {
        const resp = await authFetch<ApiResponse<unknown>>('/api/v1/sales-settings/dispatch-policy', token, {
          method: 'PUT',
          body: JSON.stringify(next),
        })
        if (resp.success) {
          // The body may be the policy or the whole settings document; re-read to be sure.
          await refetch()
          return { success: true, data: next, message: resp.message }
        }
        return { success: false, message: resp.message, errorCode: resp.errorCode }
      } catch (e) {
        return toPoolRefusal(e, 'Could not save the dispatch rules')
      }
    },
    [token, refetch],
  )

  // Memoised on the response object, so editors can re-seed only when a new policy arrives.
  const policy = useMemo(() => (res.data ? withDefaults(res.data) : null), [res.data])
  // Shared with useAtHoldingCap, so the backlog and the drawer read the same cap without a fetch.
  useEffect(() => {
    if (policy) publishDispatchPolicy(policy)
  }, [policy])
  return {
    /** The saved policy; null until loaded. */
    policy,
    /** What the board uses: the saved policy, or the plan's defaults until / unless it loads. */
    effective: policy ?? DEFAULT_DISPATCH_POLICY,
    hasLoaded: res.hasLoaded,
    error: res.error,
    refetch,
    savePolicy,
  }
}
